import type React from 'react'
import type { INavTabProps } from 'atoms/NavTab'
import type { IDropdownNavBarProps } from 'blocks/Dropdown/Templates'
import type { NavAlignItems, NavJustifyContent } from './NavGroup.h'

import {
  Children,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { NavDirection } from 'atoms/NavTab'
import { TextBoxColors } from 'atoms/TextBox'
import { DropdownNavBar } from 'blocks/Dropdown/Templates'
import { NavBackground, NavGroupGap } from './NavGroup.h'
import { NavGroupMore, NavGroupSC, NavGroupWithMoreSC } from './NavGroup.styled'

export interface INavGroup extends Omit<INavTabProps, 'title' | 'children'> {
  children: React.ReactNode | React.ReactNode[]
  gapColumn?: NavGroupGap
  gapRow?: NavGroupGap
  alignItems?: NavAlignItems
  justifyContent?: NavJustifyContent
  iconMore?: IDropdownNavBarProps['icon']
  titleMore?: IDropdownNavBarProps['title']
  colorMoreTitleHovered?: TextBoxColors
  backgroundMoreTitleHovered?: NavBackground
  colorMore?: TextBoxColors
  widthMore?: number
  maxValueTab?: number
  search?: boolean
  searchIcon?: boolean
  searchTitle?: string
  colorSearchHovered?: TextBoxColors
  colorSearchPressed?: TextBoxColors
}

export const NavGroup = ({
  children,
  gapColumn = NavGroupGap.SIZE_0,
  gapRow = NavGroupGap.SIZE_0,
  direction = NavDirection.ROW,
  widthMore = 136,
  alignItems,
  titleMore,
  justifyContent,
  color = TextBoxColors.SURFACE_DARK_7,
  iconMore,
  backgroundMoreTitleHovered = NavBackground.SURFACE_OPACITY_LIGHT_15,
  colorMoreTitleHovered = TextBoxColors.SURFACE_LIGHT_2,
  colorMore = TextBoxColors.TEXT_ICON_SECONDARY,
  maxValueTab,
  // search = true,
  // searchIcon = true,
  // searchTitle,
  // font = TextBoxTypes.Body3,
  colorSearchHovered = TextBoxColors.SURFACE_LIGHT_2,
  colorSearchPressed = TextBoxColors.BASE_MAIN_PRESSED,
}: INavGroup) => {
  const checkRef = useRef<HTMLDivElement>(null)
  const checkGroupRef = useRef<HTMLUListElement>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  const [widthChecker, setWidthChecker] = useState<number>(0)
  const [widthGroup, setWidthGroup] = useState<number>(0)
  const [widthTab, setWidthTab] = useState<number>(0)
  const [maxWidth, setMaxWidth] = useState<number | null>(null)
  const [overflow, setOverflow] = useState<boolean>(false)
  const [parentWidth, setParentWidth] = useState(0)
  const [updateGroup, setUpdateGroup] = useState<boolean>(true)
  const [listMore, setListMore] = useState<React.ReactNode[]>([])
  const components = Children.toArray(children)
  const [childrenList, setChildrenList] =
    useState<React.ReactNode[]>(components)
  const updateWidthGroup = useCallback(() => {
    if (checkRef.current) {
      setWidthChecker(checkRef.current.clientWidth)
    }
  }, [])

  useEffect(() => {
    setChildrenList(Children.toArray(children))
    setListMore([])
    updateWidthGroup()
  }, [children])

  if (direction === NavDirection.ROW) {
    const overflowHidden = useCallback(() => {
      const add = childrenList.slice(-1)
      const remove = childrenList.slice(0, childrenList.length - 1)
      setListMore([add, ...listMore])
      setChildrenList(remove)
      setOverflow(false)
    }, [overflow, widthChecker])

    const overflowShow = useCallback(() => {
      const add = listMore.slice(0, 1)
      const remove = listMore.slice(1, listMore.length)
      setChildrenList(childrenList.concat(add))
      setListMore(remove)
    }, [updateGroup])

    const isMaxValue = useCallback(() => {
      setMaxWidth(widthTab * maxValueTab!)
    }, [maxValueTab, overflow])

    useEffect(() => {
      setChildrenList(Children.toArray(children))
      setListMore([])
      updateWidthGroup()
    }, [children])

    useEffect(() => {
      setWidthGroup(
        checkGroupRef.current!.scrollWidth + moreRef.current!.scrollWidth
      )
      setOverflow(widthChecker - widthGroup < widthTab)
      setWidthTab(widthGroup / childrenList.length)
      window.addEventListener('resize', updateWidthGroup)
      return () => window.removeEventListener('resize', updateWidthGroup)
    }, [])

    useLayoutEffect(() => {
      setWidthGroup(
        checkGroupRef.current!.scrollWidth + moreRef.current!.scrollWidth
      )
      setOverflow(widthChecker - widthGroup < widthTab / 2)
      setUpdateGroup(widthChecker - widthGroup > widthTab * 2)
      updateWidthGroup()
      isMaxValue()
      setParentWidth(checkRef.current!.parentElement!.scrollWidth)
    }, [widthChecker, widthTab, overflow, childrenList])

    useEffect(() => {
      setWidthTab(widthGroup / childrenList.length)
    }, [widthGroup, childrenList.length])

    useLayoutEffect(() => {
      if (overflow && parentWidth > 768) {
        overflowHidden()
      }
    }, [overflow])

    useLayoutEffect(() => {
      if (updateGroup && parentWidth > 768) {
        overflowShow()
      }
    }, [updateGroup])
  }

  return (
    <NavGroupWithMoreSC
      colorDefault={color}
      alignItems={alignItems}
      justifyContent={justifyContent}
      direction={direction}
      gapColumn={gapColumn}
      gapRow={gapRow}
      iconMore={iconMore}
      backgroundMoreTitleHovered={backgroundMoreTitleHovered}
      colorMoreTitleHovered={colorMoreTitleHovered}
      colorMore={colorMore}
      width={widthMore}
      ref={checkRef}
      maxWidth={maxWidth!}
      colorHovered={colorSearchHovered}
      colorPressed={colorSearchPressed}
    >
      <NavGroupSC
        direction={direction}
        alignItems={alignItems}
        justifyContent={justifyContent}
        gapColumn={gapColumn}
        gapRow={gapRow}
        ref={checkGroupRef}
      >
        {childrenList}
      </NavGroupSC>
      <NavGroupMore ref={moreRef}>
        {listMore!.length > 0 && (
          <DropdownNavBar icon={iconMore} title={titleMore}>
            {listMore}
          </DropdownNavBar>
        )}
      </NavGroupMore>
    </NavGroupWithMoreSC>
  )
}
